import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Search, Plus, Upload, Loader2, Pencil, Trash2, BookOpen, FileSpreadsheet, Download,
} from "lucide-react";
import api, { API, apiError, openDownload } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TradeCombobox } from "@/components/TradeCombobox";
import { AutoQuoteBadgeToggle } from "@/components/AutoQuoteBadgeToggle";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = {
  item_code: "", description: "", unit: "", unit_rate: 0, trade: "",
  specification: "", brand: "", country_of_origin: "",
};

const CURRENCIES = [
  { symbol: "$", label: "USD  $" },
  { symbol: "AED", label: "AED  د.إ" },
  { symbol: "€", label: "EUR  €" },
  { symbol: "£", label: "GBP  £" },
  { symbol: "₹", label: "INR  ₹" },
  { symbol: "SAR", label: "SAR  ﷼" },
  { symbol: "QAR", label: "QAR  ﷼" },
];

export default function Catalogue() {
  const { user, setUser } = useAuth();
  const cur = user?.currency || "$";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [undoUnit, setUndoUnit] = useState("");
  const [taxonomy, setTaxonomy] = useState({ units: [], trades: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmImport, setConfirmImport] = useState(false);
  const fileRef = useRef();

  const load = (query = "") => {
    setItems(null);
    api.get(`/catalogue`, { params: { q: query } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(empty); setUndoUnit(""); setOpen(true); };
  const openEdit = (it) => { setEditing(it.id); setForm(it); setUndoUnit(""); setOpen(true); };

  const suggest = async () => {
    if (!form.unit && !form.description) return;
    try {
      const { data } = await api.post("/taxonomy/suggest", {
        unit: form.unit, description: form.description,
      });
      setForm((f) => ({ ...f, unit: data.unit, unit_raw: data.unit_raw, trade: f.trade || data.trade }));
      if (data.unit_raw) setUndoUnit(data.unit_raw);
    } catch { /* suggestions are a convenience, never block typing */ }
  };

  const save = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    if (!form.unit.trim()) return toast.error("Unit is required");
    if (form.unit_rate === "" || form.unit_rate === null || isNaN(Number(form.unit_rate)))
      return toast.error("Unit Rate is required");
    setSaving(true);
    const payload = { ...form, unit_rate: parseFloat(form.unit_rate) || 0 };
    try {
      if (editing) await api.put(`/catalogue/${editing}`, payload);
      else await api.post("/catalogue", payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      load(q);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/catalogue/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Item removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/catalogue/upload?replace=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Replaced catalogue — ${data.imported} items imported${data.removed ? `, ${data.removed} removed` : ""} (matched column: ${data.columns?.description})`
      );
      load(q);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportExcel = () => {
    openDownload("/catalogue/export");
  };

  const changeCurrency = async (symbol) => {
    try {
      const { data } = await api.put("/profile", { currency: symbol });
      setUser(data);
      toast.success(`Catalogue currency set to ${symbol}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not update currency");
    }
  };
  const curOptions = CURRENCIES.some((c) => c.symbol === cur)
    ? CURRENCIES : [{ symbol: cur, label: cur }, ...CURRENCIES];

  return (
    <div>
      <AutoQuoteBadgeToggle className="mb-6" />

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Pricing Database</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Catalogue</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your searchable product and rate list. {items ? `${items.length} items shown.` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onUpload} data-testid="catalogue-file-input" />
          <Button
            variant="ghost"
            data-testid="download-template-button"
            onClick={() => openDownload("/catalogue/template")}
            className="h-11 gap-2"
          >
            <Download className="w-4 h-4" /> Sample Format
          </Button>
          <Button
            variant="ghost"
            data-testid="export-catalogue-button"
            onClick={exportExcel}
            className="h-11 gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </Button>
          <Button
            variant="outline"
            data-testid="catalogue-upload-button"
            onClick={() => setConfirmImport(true)}
            disabled={uploading}
            className="h-11 gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import Excel
          </Button>
          <Button data-testid="add-item-button" onClick={openNew} className="h-11 gap-2 font-semibold">
            <Plus className="w-4 h-4" /> Add Item
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="catalogue-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by description or code..."
            className="pl-10 h-12 bg-card"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap font-semibold uppercase tracking-wider">Currency</Label>
          <Select value={cur} onValueChange={changeCurrency}>
            <SelectTrigger data-testid="catalogue-currency-select" className="h-12 w-36 bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {curOptions.map((c) => (
                <SelectItem key={c.symbol} value={c.symbol}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border">
                <th className="text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground px-4 py-3">Code</th>
                <th className="text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground px-4 py-3">Description</th>
                <th className="text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground px-4 py-3 hidden sm:table-cell">Unit</th>
                <th className="text-right font-semibold text-xs uppercase tracking-wider text-muted-foreground px-4 py-3">Rate</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="catalogue-table-body">
              {items === null ? (
                <tr><td colSpan={5} className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" /></td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground/40" />
                    <p className="mt-3 text-sm text-muted-foreground">No items found. Import an Excel file or add items manually.</p>
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it.id} data-testid={`catalogue-row-${it.id}`} className="border-b border-border last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{it.item_code || "—"}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100 max-w-md">{it.description}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell"
                        title={it.unit_raw ? `you typed “${it.unit_raw}”` : ""}>
                      {it.unit || "—"}{it.unit_raw && <span className="text-[10px] text-muted-foreground/70 ml-1">•</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">{money(it.unit_rate, cur)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-item-${it.id}`} onClick={() => openEdit(it)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`delete-item-${it.id}`} onClick={() => del(it.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={confirmImport} onOpenChange={setConfirmImport}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display">Replace your catalogue?</DialogTitle>
            <DialogDescription>
              Importing an Excel file replaces your entire pricing list.
              {items ? ` All ${items.length} existing item(s) will be deleted` : " All existing items will be deleted"} and
              only the rows in the new file will remain. Export a backup first if you need one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-import-button" onClick={() => setConfirmImport(false)}>Cancel</Button>
            <Button
              data-testid="confirm-import-button"
              onClick={() => { setConfirmImport(false); fileRef.current?.click(); }}
              className="font-semibold"
            >
              Choose file & replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit item" : "Add catalogue item"}</DialogTitle>
            <DialogDescription>Enter the product details and unit rate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Description <span className="text-destructive">*</span></Label>
              <Input data-testid="item-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 12mm TMT Steel Rebar" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Item Code</Label>
                <Input value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Unit <span className="text-destructive">*</span></Label>
                <Input data-testid="item-unit-input" value={form.unit} list="matiqs-units"
                       onChange={(e) => { setForm({ ...form, unit: e.target.value }); setUndoUnit(""); }}
                       onBlur={suggest}
                       placeholder="No. / Kg / m²" className="mt-1.5" />
                <datalist id="matiqs-units">
                  {taxonomy.units.map((u) => <option key={u} value={u} />)}
                </datalist>
                {undoUnit && (
                  <div data-testid="unit-normalised-note" className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span>was “{undoUnit}”</span>
                    <button data-testid="undo-unit-button" onClick={() => { setForm({ ...form, unit: undoUnit }); setUndoUnit(""); }}
                            className="font-semibold text-primary hover:underline">undo</button>
                  </div>
                )}
              </div>
              <div>
                <Label>Unit Rate <span className="text-destructive">*</span></Label>
                <Input data-testid="item-rate-input" type="number" step="0.01" value={form.unit_rate} onChange={(e) => setForm({ ...form, unit_rate: e.target.value })} className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label>Trade</Label>
                <div className="mt-1.5">
                  <TradeCombobox testid="item-trade-select" options={taxonomy.trades}
                                 value={form.trade || ""} onChange={(v) => setForm({ ...form, trade: v })}
                                 placeholder="Suggested from the description" />
                </div>
              </div>
            </div>
            <div>
              <Label>Specification</Label>
              <Input value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} placeholder="e.g. IS 1786 Fe500D, 4000K IP20" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Brand</Label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Country of Origin</Label>
                <Input value={form.country_of_origin} onChange={(e) => setForm({ ...form, country_of_origin: e.target.value })} className="mt-1.5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-item-button" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
